import React, { useState, useEffect } from 'react';
import { Box, Typography, Collapse, Button } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { closetAPI } from '../../utils/api';
import UsageLimits from './UsageLimits';
import { useAuth } from '../../contexts/AuthContext';

const ClosetLimits = ({ variant = 'standard' }) => {
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    const fetchLimits = async () => {
      try {
        setLoading(true);
        const response = await closetAPI.getClosetLimits();
        if (response.data && response.data.success && response.data.data) {
          setLimits(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching closet limits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLimits();
  }, [isAuthenticated]);

  if (!isAuthenticated || loading || !limits) {
    return null;
  }

  const { closet_items, uploads, analyses } = limits;
  if (!closet_items || !uploads || !analyses) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <Box sx={{ mb: 2 }}>
        <UsageLimits
          title="Closet Items"
          used={closet_items.used}
          limit={closet_items.limit}
          tooltip="Total items you can add to your closet"
          variant="compact"
        />
        <UsageLimits
          title="Daily Uploads"
          used={uploads.used}
          limit={uploads.limit}
          tooltip="Daily limit for uploading images"
          variant="compact"
        />
        <UsageLimits
          title="Daily Analyses"
          used={analyses.used}
          limit={analyses.limit}
          tooltip="Daily limit for analyzing clothing items with AI"
          variant="compact"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Button
        size="small"
        onClick={() => setExpanded(!expanded)}
        endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        variant="text"
        sx={{ mb: 1, textTransform: 'none', color: 'text.secondary' }}
      >
        <Typography variant="body2">
          {expanded ? 'Hide Usage Limits' : 'Show Usage Limits'}
        </Typography>
      </Button>
      
      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          <UsageLimits
            title="Closet Items"
            used={closet_items.used}
            limit={closet_items.limit}
            tooltip="Total items you can add to your closet"
          />
          <UsageLimits
            title="Daily Uploads"
            used={uploads.used}
            limit={uploads.limit}
            tooltip="Daily limit for uploading images"
          />
          <UsageLimits
            title="Daily Analyses" 
            used={analyses.used}
            limit={analyses.limit}
            tooltip="Daily limit for analyzing clothing items with AI"
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ClosetLimits;
